/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { closeModal, closeSecondaryModal } from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import { Offer } from 'types/entities/Offer'
import { OffersAction } from 'store/offers/OffersAction'
import { toast } from 'utils/toastUtils'
import UiDatePickerInline from 'components/Ui/UiDatePickerInline'
import Dates from 'utils/Dates'
import clsx from 'clsx'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'

interface EditOfferValidUntilModalProps {
    offer: Offer
    onSuccess?: () => void
    isSecondary?: boolean
}

const EditOfferValidUntilModal: React.FC<EditOfferValidUntilModalProps> = ({
    offer,
    onSuccess,
    isSecondary = false,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [date, setDate] = useState<Date | null>(offer.valid_until ? new Date(offer.valid_until) : null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSave = async (closeAfterSave: boolean = true) => {
        setIsSubmitting(true)
        try {
            await dispatch(
                OffersAction.updateOffer({
                    id: offer.id,
                    seller_id: offer.seller_id,
                    valid_until: date ? Dates.dayjs(date).format('YYYY-MM-DD HH:mm:ss') : null,
                })
            )
            toast.success(t('Valid until date updated'))
            if (closeAfterSave) {
                if (isSecondary) {
                    dispatch(closeSecondaryModal())
                } else {
                    dispatch(closeModal())
                }
            } else {
                setIsSubmitting(false)
            }
            if (onSuccess) onSuccess()
        } catch (e: any) {
            console.error(e)
            toast.error(t('Failed to update valid until date'))
            setIsSubmitting(false)
        }
    }

    return (
        <UiFormModalShell
            className="EditOfferValidUntilModal"
            title={t('Edit Valid Until')}
            showHeader
            footer={
                <UiFormActionsBar
                    primaryText={t('Save')}
                    onPrimary={() => handleSave(false)}
                    saveAndCloseText={t('Save and close')}
                    onSaveAndClose={() => handleSave(true)}
                    loading={isSubmitting}
                />
            }
        >
            <div className="p-4 flex flex-col gap-4">
                <UiDatePickerInline value={date} onChange={setDate} label={t('Select Date')} />

                <div className="flex flex-col gap-2">
                    <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-wider">
                        {t('Reset / No limit')}
                    </div>
                    <UiButton
                        size={UiButtonSize.Small}
                        style={UiButtonStyle.Secondary}
                        fullWidth
                        className={clsx(
                            'transition-all duration-200',
                            !date && '!bg-cta-50 !border-cta-500 !text-cta-700 dark:!bg-cta-900/30'
                        )}
                        onClick={() => setDate(null)}
                    >
                        {t('No validity limit')}
                    </UiButton>
                </div>
            </div>
        </UiFormModalShell>
    )
}

export default EditOfferValidUntilModal
